import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    useTheme,
    alpha
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { Slide } from '@mui/material';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

/**
 * Универсальное модальное окно для уведомлений об успехе или ошибке
 */
export default function NotificationModal({
    open,
    type = 'success',
    title,
    message,
    onRetry,
    onClose
}) {
    const theme = useTheme();

    // Определяем цветовую схему в зависимости от типа
    const getColorConfig = () => {
        if (type === 'success') {
            return {
                bgColor: alpha(theme.palette.success.main, 0.08),
                borderColor: theme.palette.success.main,
                iconColor: theme.palette.success.main,
                icon: <CheckCircleIcon sx={{ fontSize: 48, color: theme.palette.success.main }} />,
                defaultTitle: 'Операция выполнена успешно'
            };
        } else {
            return {
                bgColor: alpha(theme.palette.error.main, 0.08),
                borderColor: theme.palette.error.main,
                iconColor: theme.palette.error.main,
                icon: <ErrorIcon sx={{ fontSize: 48, color: theme.palette.error.main }} />,
                defaultTitle: 'Ошибка выполнения'
            };
        }
    };

    const colorConfig = getColorConfig();
    const displayTitle = title || colorConfig.defaultTitle;

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `2px solid ${colorConfig.borderColor}`,
                    boxShadow: `0 8px 32px ${alpha(colorConfig.borderColor, 0.2)}`
                }
            }}
        >
            <DialogContent sx={{
                p: 3,
                backgroundColor: colorConfig.bgColor,
                textAlign: 'center'
            }}>
                {/* Кнопка закрытия в углу */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        color: colorConfig.iconColor,
                        '&:hover': {
                            backgroundColor: alpha(colorConfig.iconColor, 0.1)
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {/* Иконка статуса */}
                <Box sx={{ mb: 2, mt: 1 }}>
                    {colorConfig.icon}
                </Box>

                {/* Заголовок */}
                <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: colorConfig.iconColor
                    }}
                >
                    {displayTitle}
                </Typography>

                {/* Сообщение */}
                <Typography
                    variant="body1"
                    sx={{
                        color: 'text.primary',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-line'
                    }}
                >
                    {message}
                </Typography>
            </DialogContent>

            <DialogActions sx={{
                p: 3,
                pt: 1,
                backgroundColor: colorConfig.bgColor,
                justifyContent: 'center',
                gap: 2
            }}>
                {type === 'error' && onRetry && (
                    <Button
                        onClick={onRetry}
                        variant="outlined"
                        color="error"
                        sx={{
                            minWidth: 120,
                            borderColor: theme.palette.error.main,
                            '&:hover': {
                                borderColor: theme.palette.error.dark,
                                backgroundColor: alpha(theme.palette.error.main, 0.04)
                            }
                        }}
                    >
                        Повторить попытку
                    </Button>
                )}

                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        minWidth: 120,
                        backgroundColor: type === 'success' ? theme.palette.success.main : theme.palette.error.main,
                        '&:hover': {
                            backgroundColor: type === 'success' ? theme.palette.success.dark : theme.palette.error.dark
                        }
                    }}
                >
                    Закрыть
                </Button>
            </DialogActions>
        </Dialog>
    );
}